// ConsultantSharePanel.jsx — 마케터 1:1 관리 세 번째 탭 '상담자배분' (2026-10-06 UX 6단계, 종전 ConsultantShareLedgerModal 창)
//   [배분 내역] 계약마다 적용·제외 + 메모 / [월별 실적] 차감 영업자·짝꿍 마케터·상담자 × 월
//   요청 주소·보내는 값은 창일 때와 같다. 위쪽 숫자 칸은 서버가 주는 이름(total·included·excluded·amount)으로 읽는다
//   (종전엔 다른 이름을 읽어 늘 0이었음). 제외 금액은 서버가 따로 안 줘서 목록에서 더한다.
import React, { useEffect, useMemo, useState } from 'react';
import { apiClient } from '../../../api/apiClient';
import { useToast } from '../../../components/Toast';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { StdPageSearch } from '../adminlayout/designStandard';
import { S } from './ConsultantShareLedgerModal.styles';
import { fmtMoney, Metric, withIntegratedMenu } from './ConsultantShareLedgerModal.helpers';
import { ConsultantShareMonthlyView } from './ConsultantShareMonthlyView';
import { ConsultantShareRow } from './ConsultantShareRow';
import { M } from './ConsultantShareMonthly.styles';

const count = (value) => `${Number(value || 0).toLocaleString('ko-KR')}건`;

export function ConsultantSharePanel({ companyCode, companyName, integratedMenuId = null }) {
  const { showToast } = useToast();
  const isCompact = useIsMobile(720);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [query, setQuery] = useState('');
  const [memoDrafts, setMemoDrafts] = useState({});
  const [error, setError] = useState('');
  const [view, setView] = useState('ledger'); // ledger = 배분 내역 / monthly = 월별 실적
  // 휴대폰은 손가락 크기(40px) — PC 는 표준 알약 그대로
  const subTab = (active) => (isCompact ? { ...M.tabBtn(active), minHeight: 40 } : M.tabBtn(active));

  async function load() {
    setLoading(true);
    setError('');
    const res = await apiClient.get(withIntegratedMenu(`/api/admin/marketer-consultant/consultant-share-ledger?company_code=${companyCode}`, integratedMenuId));
    setLoading(false);
    if (!res.ok) {
      setError(res.data?.message || '상담자배분 내역을 불러오지 못했습니다.');
      return;
    }
    const nextRows = Array.isArray(res.data?.data) ? res.data.data : [];
    setRows(nextRows);
    setSummary(res.data?.summary || null);
    const nextDrafts = {};
    nextRows.forEach((row) => { nextDrafts[row.contractId] = row.memo || ''; });
    setMemoDrafts(nextDrafts);
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyCode, integratedMenuId]);

  const filteredRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) => [
      row.clientName, row.chargedMarketerName, row.consultantName, row.ownerMarketerName, row.contractItemsText, row.memo,
    ].some((value) => String(value || '').toLowerCase().includes(q)));
  }, [query, rows]);

  const excludedAmount = useMemo(
    () => rows.filter((row) => row.decisionStatus === 'excluded').reduce((sum, row) => sum + Number(row.shareAmount || 0), 0),
    [rows],
  );

  async function saveDecision(row, nextStatus) {
    setSavingId(row.contractId);
    const res = await apiClient.put('/api/admin/marketer-consultant/consultant-share-decisions', {
      contract_id: row.contractId,
      decision_status: nextStatus,
      memo: memoDrafts[row.contractId] || null,
      ...(integratedMenuId ? { integrated_menu_id: integratedMenuId } : {}),
    });
    setSavingId(null);
    if (!res.ok) {
      showToast(res.data?.message || '상담자배분 상태 저장에 실패했습니다.', 'error');
      return;
    }
    showToast(nextStatus === 'excluded' ? '상담자배분을 제외했습니다.' : '상담자배분을 적용했습니다.', 'success');
    await load();
  }

  return (
    <div style={S.panel(isCompact)}>
      <div style={S.intro}>
        <div style={S.introTitle}>상담자배분 · {companyName || companyCode}</div>
        <div style={S.introText}>
          1:1 직원(상담자)이 도운 계약의 배분금이에요. 계약한 영업자 정산서에서는 빠지고, 그 직원의 담당 마케터 정산서에는 들어가요.
          {' '}계약마다 적용·제외를 정할 수 있어요.
        </div>
      </div>

      <div style={M.tabs} role="tablist" aria-label="상담자배분 보기">
        <button type="button" role="tab" aria-selected={view === 'ledger'} style={subTab(view === 'ledger')} onClick={() => setView('ledger')}>배분 내역</button>
        <button type="button" role="tab" aria-selected={view === 'monthly'} style={subTab(view === 'monthly')} onClick={() => setView('monthly')}>월별 실적</button>
      </div>

      {view === 'monthly' ? (
        <ConsultantShareMonthlyView companyCode={companyCode} integratedMenuId={integratedMenuId} compact={isCompact} />
      ) : (
        <>
          {error && <div style={S.error}>{error}</div>}
          <div style={S.summaryGrid}>
            <Metric label="전체" value={count(summary?.total)} />
            <Metric label="적용" value={`${count(summary?.included)} · ${fmtMoney(summary?.amount)}`} />
            <Metric label="제외" value={`${count(summary?.excluded)} · ${fmtMoney(excludedAmount)}`} />
          </div>

          <div style={S.toolbar}>
            <StdPageSearch
              isMobile={isCompact}
              mobileStyle={S.search}
              value={query}
              placeholder="매장, 영업자, 상담자 검색"
              aria-label="상담자배분 검색"
              onChange={(event) => setQuery(event.target.value)}
            />
            <button type="button" style={S.reloadBtn} onClick={load} disabled={loading}>
              {loading ? '불러오는 중' : '새로고침'}
            </button>
          </div>

          {loading && !rows.length ? (
            <div style={S.empty}>불러오는 중입니다.</div>
          ) : filteredRows.length === 0 ? (
            <div style={S.empty}>{query.trim() ? '검색 결과가 없어요.' : '상담자배분 내역이 없습니다.'}</div>
          ) : (
            <div style={S.list}>
              {filteredRows.map((row) => (
                <ConsultantShareRow
                  key={row.contractId}
                  row={row}
                  compact={isCompact}
                  memo={memoDrafts[row.contractId]}
                  onMemo={(id, value) => setMemoDrafts((prev) => ({ ...prev, [id]: value }))}
                  saving={savingId === row.contractId}
                  onDecide={saveDecision}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
